// Thêm khách tay ở trang Khách hàng: tên, SĐT, ghi chú, địa chỉ giao đầu tiên (không bắt buộc —
// thành địa chỉ mặc định). SĐT đã thuộc khách khác → báo tên khách đó + mở khách đó.

import { useState } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { ChevronDown, Plus } from "lucide-react"
import { post, ApiError } from "@/lib/api"
import { getPhoneValidationError } from "@/lib/format"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { PhoneInput } from "@/components/ui/phone-input"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { AddressDraftFields, draftError, draftPayload, emptyAddressDraft, type AddressDraft } from "./CustomerAddressDialog"

export function CustomerFormDialog({
  open,
  onOpenChange,
  onCreated,
  onOpenExisting,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: (id: string) => void
  onOpenExisting: (id: string) => void
}) {
  const qc = useQueryClient()
  const [name, setName] = useState("")
  const [phone, setPhone] = useState<string | null>(null)
  const [note, setNote] = useState("")
  const [withAddress, setWithAddress] = useState(false)
  const [addr, setAddr] = useState<AddressDraft>(emptyAddressDraft(true))
  const [error, setError] = useState<string | null>(null)
  const [owner, setOwner] = useState<{ customerId: string; name: string } | null>(null)

  const reset = () => {
    setName("")
    setPhone(null)
    setNote("")
    setWithAddress(false)
    setAddr(emptyAddressDraft(true))
    setError(null)
    setOwner(null)
  }

  const save = useMutation({
    mutationFn: () =>
      post<{ customer: { id: string; name: string } }>("/customers", {
        name: name.trim(),
        phone: phone || null,
        note: note.trim() || null,
        ...(withAddress ? { address: draftPayload(addr) } : {}),
      }),
    onSuccess: ({ customer }) => {
      void qc.invalidateQueries({ queryKey: ["customers"] })
      toast.success(`Đã thêm khách ${customer.name}`)
      reset()
      onOpenChange(false)
      onCreated(customer.id)
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === "customer.phone_exists") {
        setOwner(e.details as { customerId: string; name: string })
        setError(null)
      } else setError((e as Error).message)
    },
  })

  function submit() {
    setError(null)
    setOwner(null)
    if (!name.trim()) return setError("Nhập tên khách")
    const phoneErr = getPhoneValidationError(phone, false)
    if (phoneErr) return setError(phoneErr)
    if (withAddress) {
      const err = draftError(addr)
      if (err) return setError(err)
    }
    save.mutate()
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (save.isPending) return
        if (!v) reset()
        onOpenChange(v)
      }}
    >
      <DialogContent className="sm:max-w-2xl max-h-[92dvh] overflow-y-auto" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Thêm khách hàng</DialogTitle>
        </DialogHeader>

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block space-y-1.5">
            <span className="text-sm text-inkSoft">Tên khách *</span>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="VD: Chị Lan" className="h-11 desktop:h-9" disabled={save.isPending} />
          </label>
          <div className="space-y-1.5">
            <span className="block text-sm text-inkSoft">Số điện thoại</span>
            <PhoneInput value={phone} onValueChange={(v) => { setPhone(v); setOwner(null) }} showValidation disabled={save.isPending} />
          </div>
          <label className="block space-y-1.5 sm:col-span-2">
            <span className="text-sm text-inkSoft">Ghi chú</span>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={1000} placeholder="VD: Giao giờ hành chính" disabled={save.isPending} />
          </label>
        </div>

        {owner && (
          <div className="flex flex-wrap items-center gap-2 rounded-lg bg-warn/10 px-3 py-2 text-sm text-ink">
            <span>Số điện thoại này đã thuộc khách <b>{owner.name}</b>.</span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="ml-auto h-10 desktop:h-8"
              onClick={() => {
                reset()
                onOpenChange(false)
                onOpenExisting(owner.customerId)
              }}
            >
              Mở khách này
            </Button>
          </div>
        )}

        {withAddress ? (
          <div className="space-y-2 rounded-lg border border-rule p-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-ink">Địa chỉ giao hàng</p>
              <button type="button" className="min-h-11 desktop:min-h-0 text-xs text-muted hover:text-ink cursor-pointer" onClick={() => setWithAddress(false)}>
                Bỏ địa chỉ
              </button>
            </div>
            <AddressDraftFields value={addr} onChange={setAddr} disabled={save.isPending} showDefault={false} />
          </div>
        ) : (
          <Button type="button" variant="ghost" className="h-11 desktop:h-9 w-full justify-start gap-1.5 text-accent" onClick={() => setWithAddress(true)}>
            <Plus className="size-4" /> Thêm địa chỉ giao hàng
            <ChevronDown className="ml-auto size-4" />
          </Button>
        )}

        {error && <p className="text-sm text-danger">{error}</p>}

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" className="h-11 desktop:h-9" disabled={save.isPending} onClick={() => { reset(); onOpenChange(false) }}>
            Hủy
          </Button>
          <Button type="button" className="h-11 desktop:h-9" disabled={save.isPending} onClick={submit}>
            {save.isPending ? "Đang lưu…" : "Thêm khách"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
